import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  InventorySummary,
  InventoryProduct,
  InventoryProductsResponse,
  Pagination,
  InventoryTotals,
  InventoryMetricsResponse,
  InventoryMetric
} from '../../models/inventory.model';
import {
  GetInventorySummary,
  GetInventoryProducts,
  UpdateProductCostSettings,
  UpdateProductCost,
  SyncInventory,
  UpdateInventoryMetricsVisibility,
  UpdateInventoryMetricsVisibilityFailure,
  UpdateInventoryMetricsVisibilitySuccess,
  FetchFbtWarehousesSuccess,
  FetchFbtWarehousesFailure,
  FetchFbtWarehouses
} from './inventory.actions';
import { InventoryService } from '../../services/inventory.service';
import { MetricsService } from '../../services/metrics.service';
import { of, timer } from 'rxjs';
import {
  getInventoryMetricDisplayInfo,
  INVENTORY_METRIC_MAPPING
} from '../../features/inventory/inventory-metrics.mapping';
import { UpdateMetricsVisibility } from '../metrics-widget-config/metrics-widget-config.actions';
import { MetricsWidgetConfigState } from '../metrics-widget-config/metrics-widget-config.state';
import { environment } from '../../../environments/environment';

export interface InventoryStateModel {
  summary: InventorySummary | null;
  products: InventoryProduct[];
  pagination: Pagination | null;
  totals: InventoryTotals | null;
  metrics: { [key: string]: InventoryMetric } | null;
  metaData: any | null;
  loading: boolean;
  error: string | null;
}

@State<InventoryStateModel>({
  name: 'inventory',
  defaults: {
    summary: null,
    products: [],
    pagination: null,
    totals: null,
    metrics: null,
    metaData: null,
    loading: false,
    error: null
  }
})
@Injectable()
export class InventoryState {
  constructor(
    private inventoryService: InventoryService,
    private metricsService: MetricsService,
    private store: Store
  ) {}

  @Selector()
  static getTotals(state: InventoryStateModel): InventoryTotals | null {
    return state.totals;
  }

  @Selector()
  static getSummary(state: InventoryStateModel): InventorySummary | null {
    return state.summary;
  }

  @Selector()
  static getMetrics(state: InventoryStateModel): { [key: string]: InventoryMetric } | null {
    return state.metrics;
  }

  @Selector()
  static getSimpleVisibleMetrics(state: InventoryStateModel): InventoryMetric[] {
    if (!state.metrics) return [];

    const visibleMetrics = Object.entries(state.metrics)
      .filter(([_, metric]) => metric.visible)
      .map(([id, metric]) => ({
        ...metric,
        id
      }))
      .sort((a, b) => (a.position || 999) - (b.position || 999));

    return visibleMetrics;
  }

  @Selector()
  static getMetricsAsArray(state: InventoryStateModel): InventoryMetric[] {
    if (!state.metrics) return [];
    return Object.entries(state.metrics)
      .map(([id, metric]) => ({
        ...metric,
        id
      }))
      .sort((a, b) => (a.position || 999) - (b.position || 999));
  }

  @Selector([MetricsWidgetConfigState])
  static getVisibleMetrics(state: InventoryStateModel, configState: any): InventoryMetric[] {
    if (!state.metrics) return [];

    const sharedConfigs = configState?.metricsWidgetConfig?.metricsMap?.['inventory'] || [];

    // Повертаємо всі метрики з їх актуальними значеннями visible та сортуємо за позицією
    const visibleMetrics = Object.entries(state.metrics)
      .filter(([_, metric]) => metric.visible)
      .map(([id, metric]) => ({
        ...metric,
        id
      }));

    return visibleMetrics.sort((a, b) => {
      const aConfig = sharedConfigs.find((m: any) => m.name === a.id);
      const bConfig = sharedConfigs.find((m: any) => m.name === b.id);
      const aPos = aConfig?.position ?? 999;
      const bPos = bConfig?.position ?? 999;
      return aPos - bPos;
    });
  }

  @Selector()
  static getMetaData(state: InventoryStateModel): any | null {
    return state.metaData;
  }

  @Selector()
  static getProducts(state: InventoryStateModel): InventoryProduct[] {
    return state.products;
  }

  @Selector()
  static getPagination(state: InventoryStateModel): Pagination | null {
    return state.pagination;
  }

  @Selector()
  static isLoading(state: InventoryStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static getError(state: InventoryStateModel): string | null {
    return state.error;
  }

  @Action(GetInventorySummary)
  getInventorySummary(ctx: StateContext<InventoryStateModel>, { payload }: GetInventorySummary) {
    ctx.patchState({ loading: true, error: null });

    return this.inventoryService.getInventorySummary(payload?.search, payload?.showActiveOnly).pipe(
      map(response => response.data),
      tap((response: any) => {
        // Get current products to check if we have FBT items
        const currentState = ctx.getState();
        const hasFbtProducts = (currentState.products || []).some(
          p => p.fulfillmentType === 'FBT' || (p.fbtStock !== undefined && p.fbtStock > 0)
        );

        // Also check totalFbtStock from the response (with null check)
        const hasFbtStock = response?.totalFbtStock > 0;
        // Get widget configurations from the shared config state
        const sharedConfigs = this.store.selectSnapshot(
          state => state.metricsWidgetConfig?.metricsMap?.['inventory'] || []
        );

        // Create metrics object based on the response data and shared configs
        const metrics: { [key: string]: InventoryMetric } = {};

        // If we have shared configs, use them as the source of truth
        if (sharedConfigs.length > 0) {
          // Build metrics based on shared configs
          sharedConfigs.forEach((config: any) => {
            const metricId = config.name as string;
            const dbField =
              INVENTORY_METRIC_MAPPING[metricId as keyof typeof INVENTORY_METRIC_MAPPING];

            if (dbField) {
              const displayInfo = getInventoryMetricDisplayInfo(metricId);
              const value = response[dbField];

              // Get metric value
              const metricValue = value ?? null;

              metrics[metricId] = {
                id: metricId,
                title: config.title, // Use title from database config
                value: metricValue,
                visible: !!config.visible,
                format: displayInfo.format,
                icon: displayInfo.icon,
                position: config.position || 999,
                tooltip: displayInfo.tooltip
              };
            }
          });
        } else {
          // Fallback: Process each available metric in the mapping
          for (const [metricId, dbField] of Object.entries(INVENTORY_METRIC_MAPPING)) {
            const displayInfo = getInventoryMetricDisplayInfo(metricId);
            const value = response[dbField];

            // Set default visibility for important metrics
            const defaultVisibleIds = [
              'totalProducts',
              'totalAvailableStock',
              'totalCog',
              'totalEstSales'
            ];
            const defaultVisible = defaultVisibleIds.includes(metricId);

            let metricValue = value ?? null;

            metrics[metricId] = {
              id: metricId,
              title: displayInfo.title,
              value: metricValue,
              visible: defaultVisible,
              format: displayInfo.format,
              icon: displayInfo.icon,
              position: 999, // Default position
              tooltip: displayInfo.tooltip
            };
          }
        }

        ctx.patchState({
          metrics,
          metaData: response?.meta || null,
          loading: false
        });
      }),
      catchError(error => {
        ctx.patchState({
          error: error.message || 'Failed to load inventory summary',
          loading: false
        });
        return of(null);
      })
    );
  }

  @Action(GetInventoryProducts)
  getInventoryProducts(
    { patchState }: StateContext<InventoryStateModel>,
    { payload }: GetInventoryProducts
  ) {
    patchState({ loading: true, error: null });

    return this.inventoryService.getInventoryProducts(payload).pipe(
      tap((response: InventoryProductsResponse) => {
        patchState({
          products: response?.items || [],
          pagination: response?.pagination || { page: 1, pageSize: 10, total: 0, totalPages: 0 },
          totals: response?.totals || null,
          loading: false
        });
      }),
      catchError(error => {
        patchState({
          products: [],
          error: error.message || 'Failed to load inventory products',
          loading: false
        });
        return of(null);
      })
    );
  }

  @Action(UpdateProductCostSettings)
  updateProductCostSettings(
    { getState, patchState }: StateContext<InventoryStateModel>,
    { payload }: UpdateProductCostSettings
  ) {
    patchState({ loading: true, error: null });

    return this.inventoryService.updateProductCostSettings(payload).pipe(
      tap(() => {
        patchState({ loading: false });
        // After updating, we could refresh the product list if needed
      }),
      catchError(error => {
        patchState({
          error: error.message || 'Failed to update product settings',
          loading: false
        });
        return of(null);
      })
    );
  }

  /**
   * Calculate weighted average ROI based on COGS
   * Only includes products that have unit COG set
   */
  private calculateWeightedROI(products: InventoryProduct[]): number {
    // Filter products that have unit COG and thus valid ROI
    const productsWithCog = products.filter(
      p =>
        p.unitCog !== null &&
        p.unitCog !== undefined &&
        p.totalCog !== null &&
        p.totalCog !== undefined &&
        p.totalCog > 0
    );

    if (productsWithCog.length === 0) {
      return 0; // No products with COG, ROI is 0
    }

    // Calculate total COG for weighting
    const totalCogForRoi = productsWithCog.reduce((sum, p) => sum + (p.totalCog || 0), 0);

    if (totalCogForRoi <= 0) {
      return 0;
    }

    // Calculate weighted average ROI
    const weightedRoi = productsWithCog.reduce((sum, p) => {
      const weight = (p.totalCog || 0) / totalCogForRoi;
      const roi = p.roi || 0;
      const contribution = roi * weight;
      return sum + contribution;
    }, 0);

    return weightedRoi;
  }

  @Action(UpdateProductCost)
  updateProductCost(
    { getState, patchState, dispatch }: StateContext<InventoryStateModel>,
    { payload }: UpdateProductCost
  ) {
    // Note: Using on-the-fly allocation:
    // - Backend only updates ProductCostSettings (no aggregated_metrics recalculation)
    // - Costs are applied on-the-fly during P&L/Sales&Profit API calls
    // - Much faster updates with no heavy database operations
    const state = getState();

    // Step 1: Optimistic update of the product
    const products = [...state.products];
    const productIndex = products.findIndex(
      p => p.productId === payload.productId && (!payload.skuId || p.skuId === payload.skuId)
    );

    if (productIndex === -1) {
      console.error('Product not found for update');
      return of(null);
    }

    const oldProduct = products[productIndex];
    const updates: any = {};

    // Calculate changes
    if (payload.unitCog !== undefined) {
      updates.unitCog = payload.unitCog ?? null;
    }
    if (payload.fbsUnitStorageFee !== undefined) {
      updates.fbsUnitStorageFee = payload.fbsUnitStorageFee ?? null;
    }
    if (payload.fbsUnitFulfillmentFee !== undefined) {
      updates.fbsUnitFulfillmentFee = payload.fbsUnitFulfillmentFee ?? null;
    }
    if (payload.fbsManualShippingCostPerUnit !== undefined) {
      updates.fbsManualShippingCostPerUnit = payload.fbsManualShippingCostPerUnit ?? null;
    }

    // Apply optimistic update to product
    const optimisticProduct = {
      ...oldProduct,
      ...updates
    };

    // Optimistically calculate dependent fields
    if (payload.unitCog !== undefined) {
      // If unitCog is null, totalCog should also be null
      if (optimisticProduct.unitCog === null) {
        optimisticProduct.totalCog = null;
        optimisticProduct.estProfit = null;
        optimisticProduct.roi = null;
      } else {
        // Update total COG
        optimisticProduct.totalCog =
          (optimisticProduct.unitCog || 0) * (optimisticProduct.totalStock || 0);

        // Recalculate profit (simplified calculation)
        const unitPrice = optimisticProduct.salePrice || optimisticProduct.taxExclusivePrice || 0;
        const totalCosts =
          (optimisticProduct.unitCog || 0) +
          (optimisticProduct.commissionFee || 0) +
          (optimisticProduct.fbtFee || 0) +
          (optimisticProduct.promotionFee || 0) +
          (optimisticProduct.shippingCost || 0);
        const unitProfit = unitPrice - totalCosts;
        optimisticProduct.estProfit = unitProfit * (optimisticProduct.availableStock || 0) * 0.9; // 90% of available stock

        // NOTE: ROI is NOT recalculated here - backend will provide the correct value
        // ROI = (operatingProfit / COGS) × 100, which requires full period data
        // The frontend optimistic update cannot calculate operatingProfit correctly
      }
    }

    if (payload.fbsUnitStorageFee !== undefined) {
      // Calculate FBS Total Storage Fee (unit fee × FBS stock)
      const fbsStock = optimisticProduct.fbsStock || 0;
      if (
        optimisticProduct.fbsUnitStorageFee !== null &&
        optimisticProduct.fbsUnitStorageFee !== undefined
      ) {
        optimisticProduct.fbsTotalStorageFee = optimisticProduct.fbsUnitStorageFee * fbsStock;
      } else {
        optimisticProduct.fbsTotalStorageFee = null;
      }
      // The backend will provide the correct calculation with proper per-unit shipping costs
    }

    products[productIndex] = optimisticProduct;
    patchState({ products });

    // Step 2: Optimistically update metrics
    if (state.metrics) {
      const metrics = { ...state.metrics };

      // Calculate deltas
      const cogDelta = (optimisticProduct.totalCog || 0) - (oldProduct.totalCog || 0);
      const profitDelta = (optimisticProduct.estProfit || 0) - (oldProduct.estProfit || 0);
      const shippingDelta = (optimisticProduct.shippingCost || 0) - (oldProduct.shippingCost || 0);

      // Update relevant metrics
      if (metrics['totalCog']) {
        metrics['totalCog'] = {
          ...metrics['totalCog'],
          value: (metrics['totalCog'].value || 0) + cogDelta
        };
      }

      if (metrics['totalEstProfit']) {
        metrics['totalEstProfit'] = {
          ...metrics['totalEstProfit'],
          value: (metrics['totalEstProfit'].value || 0) + profitDelta
        };
      }

      if (metrics['totalShippingCost']) {
        metrics['totalShippingCost'] = {
          ...metrics['totalShippingCost'],
          value: (metrics['totalShippingCost'].value || 0) + shippingDelta
        };
      }

      // Recalculate average ROI using weighted average
      if (metrics['avgRoi']) {
        const weightedRoi = this.calculateWeightedROI(products);
        metrics['avgRoi'] = {
          ...metrics['avgRoi'],
          value: weightedRoi
        };
      }

      patchState({ metrics });
    }

    // Step 3: Optimistically update totals
    if (state.totals) {
      const cogDelta = (optimisticProduct.totalCog || 0) - (oldProduct.totalCog || 0);
      const profitDelta = (optimisticProduct.estProfit || 0) - (oldProduct.estProfit || 0);
      const shippingDelta = (optimisticProduct.shippingCost || 0) - (oldProduct.shippingCost || 0);

      const newTotalCog = (state.totals.totalCog || 0) + cogDelta;
      const newTotalProfit = Number(state.totals.totalEstProfit || 0) + profitDelta;
      const newTotalShipping = Number(state.totals.totalShippingCost || 0) + shippingDelta;

      // Calculate weighted ROI for totals
      const weightedRoi = this.calculateWeightedROI(products);

      patchState({
        totals: {
          ...state.totals,
          totalCog: newTotalCog,
          totalEstProfit: newTotalProfit.toString(),
          totalShippingCost: newTotalShipping.toString(),
          avgRoi: weightedRoi.toString()
        }
      });
    }

    // Step 4: Send request to server and update with real data
    return this.inventoryService.updateProductCost(payload).pipe(
      tap(response => {
        // Update with server response data
        if (response.data?.summary) {
          // Update metrics state with the new summary data
          const summaryData = response.data.summary;

          // Get current products to check if we have FBT items
          const hasFbtProducts = state.products.some(
            p => p.fulfillmentType === 'FBT' || (p.fbtStock !== undefined && p.fbtStock > 0)
          );
          const hasFbtStock = summaryData?.totalFbtStock > 0;

          // Get widget configurations from the shared config state
          const sharedConfigs = this.store.selectSnapshot(
            state => state.metricsWidgetConfig?.metricsMap?.['inventory'] || []
          );

          // Create metrics object based on the response data and shared configs
          const metrics: { [key: string]: InventoryMetric } = {};

          // If we have shared configs, use them as the source of truth
          if (sharedConfigs.length > 0) {
            // Build metrics based on shared configs
            sharedConfigs.forEach((config: any) => {
              const metricId = config.name as string;
              const dbField =
                INVENTORY_METRIC_MAPPING[metricId as keyof typeof INVENTORY_METRIC_MAPPING];

              if (dbField) {
                const displayInfo = getInventoryMetricDisplayInfo(metricId);
                const value = summaryData[dbField];

                // Get metric value
                const metricValue = value ?? null;

                metrics[metricId] = {
                  id: metricId,
                  title: config.title, // Use title from database config
                  value: metricValue,
                  visible: !!config.visible,
                  format: displayInfo.format,
                  icon: displayInfo.icon,
                  position: config.position || 999,
                  tooltip: displayInfo.tooltip
                };
              }
            });
          } else {
            // Fallback: Process each available metric in the mapping
            for (const [metricId, dbField] of Object.entries(INVENTORY_METRIC_MAPPING)) {
              const displayInfo = getInventoryMetricDisplayInfo(metricId);
              const value = summaryData[dbField];

              // Set default visibility for important metrics
              const defaultVisibleIds = [
                'totalProducts',
                'totalAvailableStock',
                'totalCog',
                'totalEstSales'
              ];
              const defaultVisible = defaultVisibleIds.includes(metricId);

              let metricValue = value ?? null;

              metrics[metricId] = {
                id: metricId,
                title: displayInfo.title,
                value: metricValue,
                visible: defaultVisible,
                format: displayInfo.format,
                icon: displayInfo.icon,
                position: 999, // Default position
                tooltip: displayInfo.tooltip
              };
            }
          }

          // Update metrics in the state
          patchState({
            metrics
          });
        }

        // If we have updated product data from the server, use it
        if (response.data?.product) {
          const updatedProduct = response.data.product;

          const products = [...getState().products];
          const productIndex = products.findIndex(
            p =>
              p.productId === updatedProduct.productId &&
              (updatedProduct.skuId ? p.skuId === updatedProduct.skuId : !p.skuId)
          );

          if (productIndex !== -1) {
            products[productIndex] = updatedProduct;
            patchState({ products });
          }
        }

        // Also update totals if provided
        if (response.data?.summary?.totalCog !== undefined) {
          // The summary is already handled by the metrics update above
          // But we might need to update totals for the grid
          const currentTotals = getState().totals;
          if (currentTotals) {
            patchState({
              totals: {
                ...currentTotals,
                totalCog: response.data.summary.totalCog,
                totalEstProfit: response.data.summary.totalEstProfit,
                avgRoi: response.data.summary.avgRoi,
                totalShippingCost: response.data.summary.totalShippingCost,
                totalEstSales: response.data.summary.totalEstSales,
                // Update individual shipping cost totals
                totalFbtShippingCost: response.data.summary.totalFbtShippingCost,
                totalFbsTikTokShippingCost: response.data.summary.totalFbsTikTokShippingCost,
                totalFbsManualShippingCost: response.data.summary.totalFbsManualShippingCost,
                // Update storage fee totals
                totalFbsTotalStorageFee: response.data.summary.totalFbsTotalStorageFee,
                totalEstFbsFulfillmentFee: response.data.summary.totalEstFbsFulfillmentFee,
                totalEstMonthlyStorageFee: response.data.summary.totalEstMonthlyStorageFee
              }
            });
          }
        }
      }),
      catchError(error => {
        console.error('Error updating product cost:', error);

        // Revert optimistic updates on error
        const revertedProducts = [...getState().products];
        if (productIndex !== -1) {
          revertedProducts[productIndex] = oldProduct;
          patchState({ products: revertedProducts });
        }

        // Could also dispatch GetInventorySummary to refresh metrics
        dispatch(new GetInventorySummary());

        return of(error);
      })
    );
  }

  // These actions are no longer needed with the simplified approach
  // @Action(UpdateProductCostSuccess)
  // @Action(RefreshProductData)

  @Action(SyncInventory)
  syncInventory({ patchState }: StateContext<InventoryStateModel>) {
    patchState({ loading: true, error: null });

    return this.inventoryService.syncInventory().pipe(
      tap(() => {
        patchState({ loading: false });
        // After syncing, we might want to refresh the data
      }),
      catchError(error => {
        patchState({
          error: error.message || 'Failed to sync inventory',
          loading: false
        });
        return of(null);
      })
    );
  }

  @Action(UpdateInventoryMetricsVisibility)
  updateInventoryMetricsVisibility(
    ctx: StateContext<InventoryStateModel>,
    { payload }: UpdateInventoryMetricsVisibility
  ) {
    // Ensure at least one metric is visible
    let metricsToUpdate = [...payload.metrics];
    const hasVisibleMetric = metricsToUpdate.some(m => m.visible);

    if (!hasVisibleMetric && metricsToUpdate.length > 0) {
      metricsToUpdate[0].visible = true;
    }

    ctx.patchState({ loading: true });

    // 1. Update local state immediately (optimistic update)
    const currentState = ctx.getState();
    if (currentState.metrics) {
      const updatedMetrics = { ...currentState.metrics };

      // Update visibility based on incoming changes
      metricsToUpdate.forEach(update => {
        const metricId = update.metricId;
        if (updatedMetrics[metricId]) {
          updatedMetrics[metricId] = {
            ...updatedMetrics[metricId],
            visible: update.visible
          };
        }
      });

      ctx.patchState({
        metrics: updatedMetrics
      });
    }

    // 2. Also update the shared metrics-widget-config state
    this.store.dispatch(
      new UpdateMetricsVisibility({
        scope: 'inventory',
        metrics: metricsToUpdate
      })
    );

    // 3. Send update to backend
    return this.metricsService.updateWidgetMetricsConfig('inventory', metricsToUpdate).pipe(
      tap(response => {
        ctx.patchState({ loading: false });
        return ctx.dispatch(new UpdateInventoryMetricsVisibilitySuccess(response.results || []));
      }),
      catchError(error => {
        const errorMessage = error.message || 'Failed to update metrics';
        // Don't dispatch failure action - just set error and reload summary
        ctx.patchState({
          loading: false,
          error: errorMessage
        });
        // Reload summary to revert optimistic updates, but preserve the error
        return ctx.dispatch(new GetInventorySummary()).pipe(
          tap(() => {
            // Restore error after summary loads (which clears it)
            ctx.patchState({ error: errorMessage });
          })
        );
      })
    );
  }

  // Update the success handler to be a no-op since we already updated the state
  @Action(UpdateInventoryMetricsVisibilitySuccess)
  updateInventoryMetricsVisibilitySuccess(
    ctx: StateContext<InventoryStateModel>,
    { results }: UpdateInventoryMetricsVisibilitySuccess
  ) {
    // Просто завантажуємо нові дані з сервера
    return ctx.dispatch(new GetInventorySummary());
  }

  // But update the failure handler to revert the change if there was an error
  @Action(UpdateInventoryMetricsVisibilityFailure)
  updateInventoryMetricsVisibilityFailure(
    { getState, patchState }: StateContext<InventoryStateModel>,
    { error }: UpdateInventoryMetricsVisibilityFailure
  ) {
    const errorMessage = 'Failed to update metrics visibility';
    patchState({ error: errorMessage, loading: false });

    // Dispatch action to reload metrics to revert the optimistic update
    return this.store.dispatch(new GetInventorySummary()).pipe(
      tap(() => {
        // Restore error after summary loads (which clears it)
        patchState({ error: errorMessage });
      })
    );
  }

  @Action(FetchFbtWarehouses)
  fetchFbtWarehouses(ctx: StateContext<InventoryStateModel>, action: FetchFbtWarehouses) {
    ctx.patchState({
      loading: true,
      error: null
    });

    return this.inventoryService.fetchFbtWarehouses(action.payload.shopId).pipe(
      tap(response => {
        ctx.dispatch(new FetchFbtWarehousesSuccess(response));
      }),
      catchError(error => {
        ctx.dispatch(new FetchFbtWarehousesFailure(error));
        return of(error);
      })
    );
  }

  @Action(FetchFbtWarehousesSuccess)
  fetchFbtWarehousesSuccess(
    ctx: StateContext<InventoryStateModel>,
    action: FetchFbtWarehousesSuccess
  ) {
    ctx.patchState({
      loading: false,
      error: null
    });
    // Handle success as needed
  }

  @Action(FetchFbtWarehousesFailure)
  fetchFbtWarehousesFailure(
    ctx: StateContext<InventoryStateModel>,
    action: FetchFbtWarehousesFailure
  ) {
    const errorMessage =
      (action.error as any)?.message || action.error || 'Failed to fetch warehouses';
    ctx.patchState({
      loading: false,
      error: errorMessage
    });
  }
}
